import { useState, FormEvent, useCallback, useEffect } from "react";
import { Link as RouterLink, useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  Link,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useAuth } from "../../hooks";
import { ResetPasswordCredentials } from "../../types/auth";
import { ErrorDisplay } from "../common/ErrorDisplay";

const ResetPasswordForm = () => {
  const navigate = useNavigate();
  const { token = "" } = useParams<{ token: string }>();
  const { resetPassword, error: authError, isLoading, clearError } = useAuth();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  // Validate password reset credentials
  const validateResetCredentials = useCallback(
    (credentials: ResetPasswordCredentials): boolean => {
      const errors: Record<string, string> = {};

      if (!credentials.token) {
        errors.token = "Reset token is required";
      }

      if (!credentials.password) {
        errors.password = "Password is required";
      } else if (credentials.password.length < 8) {
        errors.password = "Password must be at least 8 characters";
      } else if (!/[A-Z]/.test(credentials.password)) {
        errors.password = "Password must contain at least one uppercase letter";
      } else if (!/[a-z]/.test(credentials.password)) {
        errors.password = "Password must contain at least one lowercase letter";
      } else if (!/[0-9]/.test(credentials.password)) {
        errors.password = "Password must contain at least one number";
      }

      if (!credentials.confirmPassword) {
        errors.confirmPassword = "Please confirm your password";
      } else if (credentials.password !== credentials.confirmPassword) {
        errors.confirmPassword = "Passwords do not match";
      }

      setValidationErrors(errors);
      return Object.keys(errors).length === 0;
    },
    [],
  );

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setValidationErrors({});

    const credentials = { token, password, confirmPassword };
    if (!validateResetCredentials(credentials)) {
      return;
    }

    try {
      await resetPassword(credentials);
      // resetPassword rejects on failure, so reaching here means success.
      navigate("/auth/login", { state: { resetSuccess: true } });
    } catch {
      // Failure: stay on this page; authError from the provider renders above.
    }
  };

  // Clear error when password fields change
  useEffect(() => {
    if (authError && (password || confirmPassword)) {
      clearError();
    }
  }, [password, confirmPassword, authError, clearError]);

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const toggleShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: "100%" }}>
      <Typography component="h2" variant="h6" align="center" gutterBottom>
        Set New Password
      </Typography>

      <Typography variant="body2" align="center" sx={{ mb: 3 }}>
        Create a new password for your account
      </Typography>

      {authError && <ErrorDisplay error={authError} onClose={clearError} />}

      <TextField
        margin="normal"
        required
        fullWidth
        name="password"
        label="New Password"
        type={showPassword ? "text" : "password"}
        id="password"
        autoComplete="new-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={!!validationErrors.password}
        helperText={validationErrors.password}
        disabled={isLoading}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={toggleShowPassword}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <TextField
        margin="normal"
        required
        fullWidth
        name="confirmPassword"
        label="Confirm Password"
        type={showConfirmPassword ? "text" : "password"}
        id="confirmPassword"
        autoComplete="new-password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        error={!!validationErrors.confirmPassword}
        helperText={validationErrors.confirmPassword}
        disabled={isLoading}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle confirm password visibility"
                onClick={toggleShowConfirmPassword}
                edge="end"
              >
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        sx={{ mt: 3, mb: 2 }}
        disabled={isLoading}
      >
        {isLoading ? <CircularProgress size={24} /> : "Reset Password"}
      </Button>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        <Link component={RouterLink} to="/auth/login" variant="body2">
          Back to login
        </Link>
      </Box>
    </Box>
  );
};

export default ResetPasswordForm;
